/**
 * Create Tool — Source-aware creation wizard (#148).
 *
 * The user lands on a 3-card source picker (MCP / OpenAPI / Custom)
 * unless `?type=<source>` is supplied, in which case the matching flow
 * opens directly. The three flows are:
 *
 *   - MCP        → packages/web/src/components/studio/tools/create-flow-mcp.tsx
 *   - OpenAPI    → deferred V1 panel; full schema-preserving import is parked
 *   - Custom     → the legacy HTTP / Database / Custom Function form
 *                  rendered inline below (preserved verbatim so the
 *                  helper-panel + Monaco editor flow keeps working).
 *
 * The "Custom" track itself still supports two sub-types via the
 * existing radio: HTTP request or Database query.
 *
 * Search params (`type=`):
 *   - `picker` (default if omitted)  → 3-card chooser
 *   - `http` | `database`            → Custom flow with sub-type preselected
 *   - `mcp`                          → MCP wizard
 *   - `openapi`                      → OpenAPI wizard
 *   - `function`                     → legacy redirect to /studio/functions
 *
 * Save POSTs to /workspace/tools and navigates to the new tool's
 * detail page on success (not back to the list — the user almost
 * always wants to verify their config + run a test next).
 */

import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  ArrowLeft, Database, Globe, Plus, X, AlertCircle, Plug, Code,
} from 'lucide-react';
import { CreateFlowMcp } from '@/components/studio/tools/create-flow-mcp';
import {
  DeferredStudioFeature,
  STUDIO_OPENAPI_DEFERRED_MESSAGE,
} from './-deferred-feature';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { PolicyBadge } from '@/components/studio/policy-badge';
import { HelperPanel } from '@/components/studio/helper-panel';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  useCreateWorkspaceTool,
  type ToolPolicy,
} from '@/lib/use-workspace-tools';
// PR-C-MINIMAL — POLICY_DESCRIPTIONS + URL validator moved to the
// shared tools-authoring module. Inline copies removed to keep tenant
// + sysadmin authoring surfaces from drifting.
import { POLICY_DESCRIPTIONS } from '@/components/tools-authoring-shared/tools-shared.constants';
import {
  validateUrlWithTemplates,
  applyToolHelperMutation,
  mergeToolParameters,
} from '@/components/tools-authoring-shared/tools-shared.helpers';
import type { PendingMutation } from '@/lib/use-studio-helper';

const DEFAULT_OUTPUT_SCHEMA_JSON = '{\n  "type": "object",\n  "properties": {}\n}';

function defaultSideEffectsForTool(
  toolType: 'http' | 'database',
  method: string,
): string[] {
  if (toolType === 'database') return ['read'];
  return method.toUpperCase() === 'GET'
    ? ['network', 'read']
    : ['network', 'write'];
}

/** UI-side parameter shape. Persisted under workspace_tools.spec.input. */
interface ToolParameter {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'object';
  required: boolean;
  description: string;
}

export const Route = createFileRoute('/studio/tools/new')({
  validateSearch: (search: Record<string, unknown>) => ({
    // #148 — `picker` is the new default; the old default `http` is
    // still accepted (Register Tool dropdown items + the workspace
    // shortcut in routes/workspace.tsx pass it explicitly). Adding
    // `mcp` and `openapi` here opens the new wizards.
    type: (search.type as string) || 'picker',
  }),
  beforeLoad: ({ search }) => {
    // `?type=function` is the muscle-memory shortcut from the Register
    // Tool dropdown — Custom Functions now live in Studio → Functions,
    // not in the tool authoring form. Keep the redirect so old bookmarks
    // and the dropdown row work without a click-through.
    if (search.type === 'function') {
      throw redirect({ to: '/studio/functions' });
    }
  },
  component: CreateToolPageDispatch,
});

/**
 * Dispatch component — picks the correct creation flow based on the
 * `type` search param. Lives at the top so the inline Custom flow
 * doesn't have to know about its siblings.
 */
function CreateToolPageDispatch() {
  const navigate = useNavigate();
  const { type } = Route.useSearch();

  const goBackToPicker = () =>
    navigate({ to: '/studio/tools/new', search: { type: 'picker' } });
  const cancelToList = () => navigate({ to: '/studio/tools' });

  if (type === 'picker') {
    return <SourcePicker />;
  }
  if (type === 'mcp') {
    return <CreateFlowMcp onCancel={cancelToList} onBack={goBackToPicker} />;
  }
  if (type === 'openapi') {
    return (
      <DeferredStudioFeature
        title="OpenAPI tool import is deferred"
        description={STUDIO_OPENAPI_DEFERRED_MESSAGE}
        onBack={goBackToPicker}
        backLabel="Back to source picker"
      />
    );
  }
  // type === 'http' | 'database' (or any unknown) — fall through to
  // the legacy Custom Function authoring form below.
  return <CreateToolPage />;
}

/**
 * Source picker — the entry chooser introduced in #148. Three cards,
 * each routing to a dedicated creation flow.
 */
function SourcePicker() {
  const navigate = useNavigate();

  const cards: Array<{
    key: 'mcp' | 'openapi' | 'custom';
    icon: typeof Plug;
    accent: string;
    title: string;
    description: string;
    targetType: string;
    badge?: string;
  }> = [
    {
      key: 'mcp',
      icon: Plug,
      accent: 'text-blue-600',
      title: 'MCP Server tool',
      description:
        'Import one or more tools from an MCP server you have already registered in Connections. We read the server manifest and you pick which tools land in Studio.',
      targetType: 'mcp',
    },
    {
      key: 'openapi',
      icon: Code,
      accent: 'text-cyan-600',
      title: 'OpenAPI / REST endpoint',
      description:
        'Deferred for V1. Use MCP Server tools, Custom HTTP tools, or Database query tools while OpenAPI import remains parked.',
      targetType: 'openapi',
      badge: 'Deferred',
    },
    {
      key: 'custom',
      icon: Globe,
      accent: 'text-emerald-600',
      title: 'Custom HTTP / Database tool',
      description:
        'Author a direct HTTP request or parameterized SQL query by hand. Use this when there is no spec and no MCP server — Tool Helper can prefill the manifest.',
      targetType: 'http',
      badge: 'HTTP + Database',
    },
  ];

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => navigate({ to: '/studio/tools' })}
            aria-label="Back to studio"
            title="Back"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div>
            <h1 className="text-lg font-semibold">Register Tool</h1>
            <p className="text-xs text-muted-foreground">
              Pick a source — each flow generates a row in the tool catalog.
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-auto p-6 max-w-4xl mx-auto w-full space-y-3">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              type="button"
              disabled={c.key === 'openapi'}
              title={c.key === 'openapi' ? STUDIO_OPENAPI_DEFERRED_MESSAGE : undefined}
              onClick={() =>
                navigate({
                  to: '/studio/tools/new',
                  search: { type: c.targetType },
                })
              }
              className={cn(
                'w-full text-left p-4 rounded-lg border bg-card hover:border-primary/50 hover:bg-primary/5 transition-colors',
                'flex items-start gap-3',
                c.key === 'openapi' &&
                  'cursor-not-allowed opacity-60 hover:border-border hover:bg-card',
              )}
            >
              <div
                className={cn(
                  'h-10 w-10 rounded-md border flex items-center justify-center shrink-0 bg-muted/40',
                )}
              >
                <Icon className={cn('h-5 w-5', c.accent)} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold">{c.title}</h2>
                  {c.badge && (
                    <Badge variant="secondary" className="text-[10px]">
                      {c.badge}
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                  {c.description}
                </p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CreateToolPage() {
  const navigate = useNavigate();
  const { type: initialType } = Route.useSearch();
  const createTool = useCreateWorkspaceTool();

  // Form state
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [toolType, setToolType] = useState<'http' | 'database'>(initialType === 'database' ? 'database' : 'http');

  // HTTP config
  const [httpMethod, setHttpMethod] = useState<string>('GET');
  const [httpUrl, setHttpUrl] = useState('');
  const [httpPathTemplate, setHttpPathTemplate] = useState('');
  const [httpHeaders, setHttpHeaders] = useState<{ key: string; value: string }[]>([]);
  const [httpBody, setHttpBody] = useState('');
  const [outputSchemaJson, setOutputSchemaJson] = useState(DEFAULT_OUTPUT_SCHEMA_JSON);

  // Database config
  const [dbConnection, setDbConnection] = useState('');
  const [sqlQuery, setSqlQuery] = useState('');

  // Parameters
  const [parameters, setParameters] = useState<ToolParameter[]>([]);

  // Policy
  const [policy, setPolicy] = useState<ToolPolicy>('auto_approve');
  const [sideEffects, setSideEffects] = useState<string[]>([]);

  // Round 5 fix #260 — surface duplicate-name + URL validation errors
  // inline on the offending field rather than as a generic toast.
  // Round 6 STUDIO-R6-001 — also surface missing database `connection`
  // inline.
  const [formError, setFormError] = useState<
    { field: 'name' | 'baseUrl' | 'connection'; message: string } | null
  >(null);

  const namespacePrefix = toolType === 'database' ? 'db' : 'custom';
  const fullName = name.trim() ? `${namespacePrefix}.${name.trim()}` : '';

  // Round 5 fix #259 — client-side URL validation, with template
  // placeholder substitution before parsing so users can author URLs
  // like `https://api.example.com/v1/{{resource}}`. The server runs
  // the same check (defense in depth). PR-C-MINIMAL — pulled into the
  // shared tools-authoring helpers (`validateUrlWithTemplates`).
  const httpUrlError = useMemo<string | null>(() => {
    if (toolType !== 'http') return null;
    return validateUrlWithTemplates(httpUrl);
  }, [toolType, httpUrl]);

  const outputSchemaError = useMemo<string | null>(() => {
    if (!outputSchemaJson.trim()) return null;
    try {
      const parsed = JSON.parse(outputSchemaJson);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return 'Output schema must be a JSON object.';
      }
      return null;
    } catch (e) {
      return `Output schema is not valid JSON: ${
        e instanceof Error ? e.message : 'parse error'
      }`;
    }
  }, [outputSchemaJson]);

  // Round 6 fix STUDIO-R6-001 + NEW-MAJOR-2 horizontal sweep — every
  // tool source's required fields must gate Create. Database tools
  // were missing `dbConnection`; tool parameters were already required
  // to be non-empty AND have a name (added below). Detection here
  // mirrors the per-source server validation in
  // packages/api/src/routes/v1/workspace-tools.ts. Keep the two in sync.
  const blankParameterNames = parameters.length > 0 && parameters.some((p) => !p.name.trim());

  const canSave = name.trim() && displayName.trim() && description.trim() &&
    (toolType === 'http'
      ? httpUrl.trim() && !httpUrlError
      : sqlQuery.trim() && dbConnection.trim()) &&
    parameters.length > 0 && !blankParameterNames &&
    !outputSchemaError;

  // Parameter management
  const addParameter = () => {
    setParameters(prev => [...prev, { name: '', type: 'string', required: false, description: '' }]);
  };

  const updateParameter = (index: number, field: keyof ToolParameter, value: string | boolean) => {
    setParameters(prev => prev.map((p, i) => i === index ? { ...p, [field]: value } : p));
  };

  const removeParameter = (index: number) => {
    setParameters(prev => prev.filter((_, i) => i !== index));
  };

  // Header management
  const addHeader = () => setHttpHeaders(prev => [...prev, { key: '', value: '' }]);
  const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
    setHttpHeaders(prev => prev.map((h, i) => i === index ? { ...h, [field]: value } : h));
  };
  const removeHeader = (index: number) => setHttpHeaders(prev => prev.filter((_, i) => i !== index));

  const handleSave = async () => {
    if (!canSave) return;

    // Build the spec envelope expected by the workspace_tools
    // compatibility view trigger. It unwraps { input, output } into
    // tools.input_schema / tools.output_schema.
    const properties: Record<string, { type: string; description: string }> = {};
    const required: string[] = [];
    for (const p of parameters) {
      properties[p.name.trim()] = {
        type: p.type,
        description: p.description.trim() || `Parameter ${p.name}`,
      };
      if (p.required) required.push(p.name.trim());
    }
    const inputSchema = { type: 'object', properties, required };
    let outputSchema: unknown = { type: 'object', properties: {} };
    if (outputSchemaJson.trim()) {
      try {
        const parsed = JSON.parse(outputSchemaJson);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
          toast.error('Output schema must be a JSON object.');
          return;
        }
        outputSchema = parsed;
      } catch (e) {
        toast.error('Output schema is not valid JSON', {
          description: e instanceof Error ? e.message : 'parse error',
        });
        return;
      }
    }
    const spec = { input: inputSchema, output: outputSchema };
    const effectiveSideEffects =
      sideEffects.length > 0
        ? sideEffects
        : defaultSideEffectsForTool(toolType, httpMethod);

    const handlerConfig =
      toolType === 'http'
        ? {
            method: httpMethod,
            baseUrl: httpUrl.trim(),
            // pathTemplate must reach handler_config → source_config so the
            // runtime HTTP adapter can inject {{param}} placeholders (a
            // parameterized GET/DELETE drops its args without it).
            pathTemplate: httpPathTemplate.trim() || undefined,
            headers: httpHeaders
              .filter((h) => h.key.trim())
              .reduce<Record<string, string>>((acc, h) => {
                acc[h.key.trim()] = h.value;
                return acc;
              }, {}),
            body: httpBody.trim() || null,
            category: category.trim() || null,
          }
        : {
            connection: dbConnection.trim(),
            query: sqlQuery.trim(),
            category: category.trim() || null,
          };

    setFormError(null);
    try {
      const created = await createTool.mutateAsync({
        name: name.trim(),
        displayName: displayName.trim(),
        description: description.trim(),
        toolSource: toolType === 'http' ? 'custom' : 'database',
        spec,
        handlerConfig,
        namespace: namespacePrefix,
        policy,
        sideEffects: effectiveSideEffects,
      });
      toast.success(`Tool "${fullName}" registered`);
      // Navigate to the new tool's detail page so the user can verify
      // config + run a test before activating.
      navigate({
        to: '/studio/tools/$toolId',
        params: { toolId: created.id },
      });
    } catch (err) {
      // Round 5 fix #259 / #260 — the API now returns typed
      //   { error: { code, message, field? } }
      // payloads for INVALID_URL (400) and DUPLICATE_TOOL_NAME (409).
      // The shared `apiClient` attaches the parsed body to
      //   (error as any).data
      // (see packages/web/src/lib/api-client.ts:200-207). Surface
      // field-scoped errors inline on the offending input instead of
      // the prior generic "An internal error occurred" toast (the
      // round-5 minor bug #260 was that the duplicate-name pop
      // blamed nothing visible).
      const data = (err as { data?: { error?: { code?: string; message?: string; field?: string } } })?.data;
      const apiError = data?.error;
      if (apiError?.code === 'DUPLICATE_TOOL_NAME') {
        setFormError({ field: 'name', message: apiError.message ?? 'Slug already exists in your tenant.' });
        toast.error('Slug already exists', { description: apiError.message });
        return;
      }
      if (apiError?.code === 'INVALID_URL') {
        setFormError({ field: 'baseUrl', message: apiError.message ?? 'URL is invalid.' });
        toast.error('Invalid URL', { description: apiError.message });
        return;
      }
      // Round 6 fix STUDIO-R6-001 — server returns 400 MISSING_CONNECTION
      // when a database tool has no `handlerConfig.connection`. Surface
      // it on the Database Connection field, same pattern as INVALID_URL.
      if (apiError?.code === 'MISSING_CONNECTION') {
        setFormError({
          field: 'connection',
          message:
            apiError.message ??
            'Pick a database connection — required for Database tools.',
        });
        toast.error('Database connection required', { description: apiError.message });
        return;
      }
      toast.error('Could not register tool', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-border/40 bg-background px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate({ to: '/studio/tools' })} aria-label="Back to studio" title="Back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Separator orientation="vertical" className="h-5" />
          <div>
            <h1 className="text-lg font-semibold">Register Custom Tool</h1>
            <p className="text-xs text-muted-foreground">Create an HTTP request tool or database query tool · Tool Helper available on the right</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate({ to: '/studio/tools' })}>Cancel</Button>
          <Button
            size="sm"
            disabled={!canSave || createTool.isPending}
            onClick={handleSave}
          >
            {createTool.isPending ? 'Creating…' : 'Create Tool'}
          </Button>
        </div>
      </div>

      {/* Two-column layout: form on the left, Tool Helper chat on the
          right. Phase 4B (#147) — the helper has structural tenant
          metadata only and helps draft the form fields.
          Round 3 fix #147 — the panel was previously gated to `lg:`
          (≥1024px) breakpoint with `hidden lg:block`, so narrower test
          viewports never rendered it (Codex found it absent). Lower
          breakpoint to `md:` (≥768px) and drop the `hidden lg:block`
          off the aside so the panel is always in the DOM, stacking
          below the form on small screens. */}
      <div className="flex-1 overflow-hidden grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(320px,420px)]">
      <div className="overflow-auto p-6 max-w-3xl space-y-6">

        {/* Section 1: Identity */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Identity</CardTitle>
            <CardDescription className="text-xs">Name and description for this tool</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1.5">
              {/* fix-wave-4 #961 — label htmlFor + input id linkage so
                  Playwright `getByLabel(/Tool Name/i)` resolves to the
                  expected Input and not a random matching control. */}
              <Label htmlFor="tools-new-name" className="text-xs">Tool Name<span className="text-red-500 ml-0.5">*</span></Label>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-[10px] font-mono shrink-0">{namespacePrefix}.</Badge>
                <Input
                  id="tools-new-name"
                  aria-label="Tool Name"
                  placeholder="e.g., check_valve_status"
                  value={name}
                  onChange={e => {
                    setName(e.target.value.replace(/[^a-z0-9_]/g, ''));
                    if (formError?.field === 'name') setFormError(null);
                  }}
                  className={cn('font-mono', formError?.field === 'name' && 'border-destructive focus-visible:ring-destructive')}
                  maxLength={50}
                  aria-invalid={formError?.field === 'name' || undefined}
                  aria-describedby={formError?.field === 'name' ? 'tool-name-error' : undefined}
                />
              </div>
              {/* Round 5 fix #260 — inline duplicate-name error on the
                  Tool Name field (was generic "internal error" toast). */}
              {formError?.field === 'name' && (
                <p id="tool-name-error" className="text-[11px] text-destructive flex items-start gap-1">
                  <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                  <span>{formError.message}</span>
                </p>
              )}
              {fullName && !formError && <p className="text-[10px] text-muted-foreground">Full name: <span className="font-mono">{fullName}</span></p>}
            </div>

            <div className="space-y-1.5">
              {/* fix-wave-4 #961 — htmlFor linkage. */}
              <Label htmlFor="tools-new-display-name" className="text-xs">Display Name<span className="text-red-500 ml-0.5">*</span></Label>
              <Input
                id="tools-new-display-name"
                aria-label="Display Name"
                placeholder="e.g., Check Valve Status"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                maxLength={60}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Description</Label>
              <Textarea
                placeholder="Brief description of what this tool does..."
                value={description}
                onChange={e => setDescription(e.target.value)}
                rows={2}
                maxLength={300}
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Input
                placeholder="e.g., operations, maintenance, monitoring"
                value={category}
                onChange={e => setCategory(e.target.value)}
                maxLength={30}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Type */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Tool Type</CardTitle>
            <CardDescription className="text-xs">How this tool executes</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={toolType}
              onValueChange={v => setToolType(v as 'http' | 'database')}
              className="space-y-2"
            >
              {([
                { value: 'http', label: 'HTTP Request', desc: 'Make HTTP requests to external APIs or internal services', icon: Globe },
                { value: 'database', label: 'Database Query', desc: 'Execute parameterized SQL queries against configured databases', icon: Database },
              ] as const).map(option => (
                <label
                  key={option.value}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    toolType === option.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                  )}
                >
                  <RadioGroupItem value={option.value} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <option.icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">{option.label}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{option.desc}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Section 3: HTTP Config */}
        {toolType === 'http' && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">HTTP Configuration</CardTitle>
              <CardDescription className="text-xs">Request details — use {'{{variable}}'} syntax for dynamic values</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-28">
                  <Label htmlFor="http-method" className="text-xs">Method</Label>
                  <Select value={httpMethod} onValueChange={setHttpMethod}>
                    <SelectTrigger id="http-method" aria-label="HTTP method">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="GET">GET</SelectItem>
                      <SelectItem value="POST">POST</SelectItem>
                      <SelectItem value="PUT">PUT</SelectItem>
                      <SelectItem value="PATCH">PATCH</SelectItem>
                      <SelectItem value="DELETE">DELETE</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  {/* fix-wave-4 #961 — htmlFor linkage. */}
                  <Label htmlFor="tools-new-url" className="text-xs">URL<span className="text-red-500 ml-0.5">*</span></Label>
                  <Input
                    id="tools-new-url"
                    aria-label="URL"
                    placeholder="https://api.example.com/v1/{{resource}}"
                    value={httpUrl}
                    onChange={e => {
                      setHttpUrl(e.target.value);
                      if (formError?.field === 'baseUrl') setFormError(null);
                    }}
                    className={cn(
                      'font-mono',
                      (httpUrlError || formError?.field === 'baseUrl') && 'border-destructive focus-visible:ring-destructive',
                    )}
                    aria-invalid={(httpUrlError || formError?.field === 'baseUrl') ? true : undefined}
                    aria-describedby={
                      httpUrlError || formError?.field === 'baseUrl' ? 'tool-url-error' : undefined
                    }
                  />
                  {/* Round 5 fix #259 — inline URL syntax error.
                      Server replays the same Zod check so direct
                      callers can't slip a bad URL past the form. */}
                  {(httpUrlError || formError?.field === 'baseUrl') && (
                    <p id="tool-url-error" className="text-[11px] text-destructive flex items-start gap-1 mt-1">
                      <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                      <span>{httpUrlError ?? formError?.message}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Path template — {{param}} path/query appended to the base URL. */}
              <div>
                <Label htmlFor="tools-new-path-template" className="text-xs">
                  Path template
                </Label>
                <Input
                  id="tools-new-path-template"
                  aria-label="Path template"
                  placeholder="/v1/forecast?latitude={{lat}}&longitude={{lon}}"
                  value={httpPathTemplate}
                  onChange={(e) => setHttpPathTemplate(e.target.value)}
                  className="font-mono"
                />
                <p className="text-[11px] text-muted-foreground mt-1">
                  Appended to the base URL at call time. Use <code>{'{{param}}'}</code> placeholders whose
                  names match your parameters. Required for a GET/DELETE that takes parameters — those
                  requests send no body.
                </p>
              </div>

              {/* Headers */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Headers</Label>
                  <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={addHeader}>
                    <Plus className="h-3 w-3" /> Add Header
                  </Button>
                </div>
                {httpHeaders.map((header, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      placeholder="Header name"
                      value={header.key}
                      onChange={e => updateHeader(i, 'key', e.target.value)}
                      className="font-mono text-xs flex-1"
                    />
                    <Input
                      placeholder="Value"
                      value={header.value}
                      onChange={e => updateHeader(i, 'value', e.target.value)}
                      className="font-mono text-xs flex-1"
                    />
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => removeHeader(i)} aria-label="Remove header" title="Remove header">
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>

              {/* Body */}
              {(httpMethod === 'POST' || httpMethod === 'PUT' || httpMethod === 'PATCH') && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Request Body</Label>
                  <Textarea
                    placeholder={'{\n  "key": "{{value}}"\n}'}
                    value={httpBody}
                    onChange={e => setHttpBody(e.target.value)}
                    rows={5}
                    className="font-mono text-xs"
                  />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Section 4: Database Config */}
        {toolType === 'database' && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Database Configuration</CardTitle>
              <CardDescription className="text-xs">SQL query with {'{{variable}}'} placeholders for parameters</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="db-connection" className="text-xs">
                  Database Connection<span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Select
                  value={dbConnection}
                  onValueChange={(v) => {
                    setDbConnection(v);
                    if (formError?.field === 'connection') setFormError(null);
                  }}
                >
                  <SelectTrigger
                    id="db-connection"
                    aria-label="Database Connection"
                    aria-invalid={
                      formError?.field === 'connection' ||
                      (!dbConnection.trim() && (sqlQuery.trim() || parameters.length > 0))
                        ? true
                        : undefined
                    }
                    aria-describedby={
                      formError?.field === 'connection' ? 'db-connection-error' : undefined
                    }
                    className={cn(
                      formError?.field === 'connection' &&
                        'border-destructive focus-visible:ring-destructive',
                    )}
                  >
                    <SelectValue placeholder="Select database..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cmms-ro">CMMS Database (read-only)</SelectItem>
                    <SelectItem value="historian">Process Historian</SelectItem>
                    <SelectItem value="erp-ro">ERP Database (read-only)</SelectItem>
                  </SelectContent>
                </Select>
                {/* Round 6 fix STUDIO-R6-001 — inline missing-connection
                    error. Server replays the same check (defense in depth). */}
                {formError?.field === 'connection' && (
                  <p id="db-connection-error" className="text-[11px] text-destructive flex items-start gap-1">
                    <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                    <span>{formError.message}</span>
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                {/* fix-wave-4 #1034 (STUDIO-HELPER-010) — Pre-fix the
                    Label had no `htmlFor` linkage to the Textarea, so
                    Playwright's `getByLabel(/SQL/i)` resolved against
                    the nearest accessible-name-matching control which
                    happened to be a sibling radio button on the form.
                    Wiring `htmlFor` + `id` makes the SQL textarea the
                    canonical "SQL Query" labelled control and lets the
                    matrix probe (and screen readers) reach the textarea
                    via its label. */}
                <Label htmlFor="tools-new-sql-query" className="text-xs">SQL Query<span className="text-red-500 ml-0.5">*</span></Label>
                <Textarea
                  id="tools-new-sql-query"
                  aria-label="SQL Query"
                  placeholder={"SELECT equipment_tag, downtime_hours, reason\nFROM downtime_log\nWHERE equipment_tag = '{{equipmentTag}}'\n  AND date >= '{{startDate}}'"}
                  value={sqlQuery}
                  onChange={e => setSqlQuery(e.target.value)}
                  rows={8}
                  className="font-mono text-xs"
                />
                <p className="text-[10px] text-muted-foreground">Use {'{{variableName}}'} for parameterized inputs. Never allow raw SQL injection.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Section 5: Parameters */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-sm">Input Parameters</CardTitle>
                <CardDescription className="text-xs">Define the inputs this tool accepts — at least one required</CardDescription>
              </div>
              <Button variant="outline" size="sm" className="gap-1 h-7 text-xs" onClick={addParameter}>
                <Plus className="h-3 w-3" /> Add Parameter
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {parameters.length > 0 ? (
              <div className="space-y-3">
                {parameters.map((param, i) => {
                  // Round 6 fix STUDIO-R6-002 (horizontal sweep) —
                  // tool parameter rows must have a non-empty name,
                  // mirroring the prompt + skill variable editors.
                  // canSave already gates Create on this, but the
                  // user gets no visual hint why save is greyed out
                  // — surface an inline error per row.
                  const blankParamName = !param.name.trim();
                  return (
                  <div key={i} className={cn(
                    'p-3 rounded-lg border space-y-2',
                    blankParamName && 'border-destructive/40',
                  )}>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <Input
                          placeholder="parameter_name"
                          value={param.name}
                          onChange={e => updateParameter(i, 'name', e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                          className={cn(
                            'font-mono text-xs h-8',
                            blankParamName && 'border-destructive focus-visible:ring-destructive',
                          )}
                          aria-invalid={blankParamName || undefined}
                          aria-label={`Name for parameter ${i + 1}`}
                          aria-describedby={blankParamName ? `tool-param-name-error-${i}` : undefined}
                        />
                        {blankParamName && (
                          <p id={`tool-param-name-error-${i}`} className="text-[10px] text-destructive flex items-start gap-1 mt-1">
                            <AlertCircle className="h-2.5 w-2.5 mt-0.5 shrink-0" />
                            <span>Name is required.</span>
                          </p>
                        )}
                      </div>
                      <Select value={param.type} onValueChange={v => updateParameter(i, 'type', v)}>
                        <SelectTrigger
                          className="w-28 h-8 text-xs"
                          aria-label={
                            param.name
                              ? `Type for parameter ${param.name}`
                              : `Type for parameter ${i + 1}`
                          }
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="string">string</SelectItem>
                          <SelectItem value="number">number</SelectItem>
                          <SelectItem value="boolean">boolean</SelectItem>
                          <SelectItem value="array">array</SelectItem>
                          <SelectItem value="object">object</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex items-center gap-1.5">
                        <Switch
                          checked={param.required as boolean}
                          onCheckedChange={v => updateParameter(i, 'required', v)}
                          className="scale-75"
                        />
                        <span className="text-[10px] text-muted-foreground">Required</span>
                      </div>
                      <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => removeParameter(i)} aria-label="Remove parameter" title="Remove parameter">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <Input
                      placeholder="Description of this parameter"
                      value={param.description}
                      onChange={e => updateParameter(i, 'description', e.target.value)}
                      className="text-xs h-8"
                      aria-label={
                        param.name
                          ? `Description for parameter ${param.name}`
                          : `Description for parameter ${i + 1}`
                      }
                    />
                  </div>
                  );
                })}
              </div>
            ) : (
              <div className="border border-dashed rounded-lg p-6 text-center">
                <p className="text-sm text-muted-foreground">No parameters defined</p>
                <p className="text-xs text-muted-foreground/70 mt-1">Add at least one parameter for agents to pass when calling this tool</p>
                <Button variant="outline" size="sm" className="mt-3 gap-1" onClick={addParameter}>
                  <Plus className="h-3 w-3" /> Add Parameter
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Section 6: Output Schema */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Output Schema</CardTitle>
            <CardDescription className="text-xs">
              Optional JSON Schema describing the response agents can expect.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <Label htmlFor="tool-output-schema" className="text-xs">
              Response schema JSON
            </Label>
            <Textarea
              id="tool-output-schema"
              value={outputSchemaJson}
              onChange={(e) => setOutputSchemaJson(e.target.value)}
              rows={6}
              className={cn(
                'font-mono text-xs',
                outputSchemaError && 'border-destructive focus-visible:ring-destructive',
              )}
              aria-invalid={outputSchemaError ? true : undefined}
              aria-describedby={outputSchemaError ? 'tool-output-schema-error' : undefined}
              spellCheck={false}
            />
            {outputSchemaError && (
              <p id="tool-output-schema-error" className="text-[11px] text-destructive flex items-start gap-1">
                <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{outputSchemaError}</span>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Section 7: Policy */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Execution Policy</CardTitle>
            <CardDescription className="text-xs">How should this tool be executed when called by an agent?</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup value={policy} onValueChange={v => setPolicy(v as ToolPolicy)} className="space-y-2">
              {(['auto_approve', 'require_approval', 'forbidden'] as ToolPolicy[]).map(p => (
                <label
                  key={p}
                  className={cn(
                    'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                    policy === p ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'
                  )}
                >
                  <RadioGroupItem value={p} className="mt-0.5" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium capitalize">{p.replace(/_/g, ' ')}</span>
                      <PolicyBadge policy={p} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{POLICY_DESCRIPTIONS[p]}</p>
                  </div>
                </label>
              ))}
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Section 7: Review */}
        {canSave && (
          <Card className="border-primary/20 bg-primary/5">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Review</CardTitle>
              <CardDescription className="text-xs">Verify your configuration before creating</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Full Name</p>
                  <p className="font-mono text-xs">{fullName}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Display Name</p>
                  <p>{displayName}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Type</p>
                  <p>{toolType === 'http' ? `HTTP ${httpMethod}` : 'Database Query'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Target</p>
                  <p className="font-mono text-xs truncate">{toolType === 'http' ? httpUrl : dbConnection || '(not selected)'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Parameters</p>
                  <p>{parameters.length} ({parameters.filter(p => p.required).length} required)</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase">Policy</p>
                  <PolicyBadge policy={policy} />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      {/* Right column — Tool Helper. Phase 4B (#147). Round 3: the
          aside is in the DOM at all viewports; stacks below the form
          on mobile, sits side-by-side on md+. Has its own min-height
          so the chat composer is reachable on narrow screens. */}
      <aside className="border-l md:border-l border-t md:border-t-0 overflow-hidden min-h-[400px]">
        <HelperPanel
          kind="tool_helper"
          intro="Describe the tool you want and I'll help draft the manifest — name, schemas, policy, and risk markers. I look at your tenant catalog metadata only; I don't run code."
          className="h-full rounded-none border-0"
          // #1092 — Pre-fix this panel was mounted WITHOUT an
          // `onApplyMutation` callback, so clicking Accept on a
          // helper-drafted mutation card consumed the card but
          // never wrote any of the proposed metadata to the form.
          // The form stayed blank and Create Tool remained
          // disabled — the helper appeared to work but produced
          // zero end-user value.
          //
          // Fix mirrors the PR #1159 pattern used for Prompt /
          // Skill Helper Accept: translate each mutation tool call
          // into a `Partial<ToolHelperDraft>` patch via the pure
          // `applyToolHelperMutation` helper, then apply each
          // field via its own functional setState. Functional
          // setState never reads stale closure values, so a
          // multi-mutation batch (e.g. `set_metadata` followed by
          // `set_source` + `set_policy` from one Accept-All click)
          // composes atomically instead of clobbering each other.
          canvasState={{
            name,
            displayName,
            description,
            category,
            toolType,
            httpMethod,
            httpUrl,
            httpHeaders,
            httpBody,
            outputSchemaJson,
            sideEffects,
            sqlQuery,
            dbConnection,
            policy,
            parameterCount: parameters.length,
          } as Record<string, unknown>}
          onApplyMutation={(m: PendingMutation) => {
            const patch = applyToolHelperMutation(
              m.toolName,
              m.args as Record<string, unknown>,
            );
            if (Object.keys(patch).length === 0) return;
            // Apply each touched field through its own setter so the
            // existing state-shape contract (independent useState
            // slots) is preserved. Each setter is itself a
            // functional setState so a same-tick second mutation
            // (Accept-All loop) reads the freshly updated value.
            if (patch.name !== undefined) {
              const next = patch.name;
              setName((prev) => (prev !== next ? next : prev));
              if (formError?.field === 'name') setFormError(null);
            }
            if (patch.displayName !== undefined) {
              const next = patch.displayName;
              setDisplayName((prev) => (prev !== next ? next : prev));
            }
            if (patch.description !== undefined) {
              const next = patch.description;
              setDescription((prev) => (prev !== next ? next : prev));
            }
            if (patch.category !== undefined) {
              const next = patch.category;
              setCategory((prev) => (prev !== next ? next : prev));
            }
            if (patch.toolType !== undefined) {
              const next = patch.toolType;
              setToolType((prev) => (prev !== next ? next : prev));
            }
            if (patch.httpMethod !== undefined) {
              const next = patch.httpMethod;
              setHttpMethod((prev) => (prev !== next ? next : prev));
            }
            if (patch.httpUrl !== undefined) {
              const next = patch.httpUrl;
              setHttpUrl((prev) => (prev !== next ? next : prev));
              if (formError?.field === 'baseUrl') setFormError(null);
            }
            if (patch.httpPathTemplate !== undefined) {
              const next = patch.httpPathTemplate;
              setHttpPathTemplate((prev) => (prev !== next ? next : prev));
            }
            if (patch.httpHeaders !== undefined) {
              const next = patch.httpHeaders;
              setHttpHeaders(next);
            }
            if (patch.httpBody !== undefined) {
              const next = patch.httpBody;
              setHttpBody((prev) => (prev !== next ? next : prev));
            }
            if (patch.outputSchemaJson !== undefined) {
              const next = patch.outputSchemaJson;
              setOutputSchemaJson((prev) => (prev !== next ? next : prev));
            }
            if (patch.sideEffects !== undefined) {
              const next = patch.sideEffects;
              setSideEffects(next);
            }
            if (patch.sqlQuery !== undefined) {
              const next = patch.sqlQuery;
              setSqlQuery((prev) => (prev !== next ? next : prev));
            }
            if (patch.dbConnection !== undefined) {
              const next = patch.dbConnection;
              setDbConnection((prev) => (prev !== next ? next : prev));
              if (formError?.field === 'connection') setFormError(null);
            }
            if (patch.policy !== undefined) {
              const next = patch.policy;
              setPolicy((prev) => (prev !== next ? next : prev));
            }
            if (patch.parameters !== undefined) {
              const next = patch.parameters;
              setParameters((prev) => mergeToolParameters(prev, next));
            }
          }}
        />
      </aside>
      </div>
    </div>
  );
}
